import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Rocket, Headphones, Wallet, ArrowRight } from 'lucide-react';

export default function TrialModal({ open, onClose, plan, onPayNow }) {
  if (!plan) return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          onClick={onClose}
          data-testid="trial-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 0.9, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[hsl(var(--card))] border border-border rounded-3xl p-7 sm:p-8 shadow-2xl"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
              aria-label="Close"
              data-testid="trial-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/40 flex items-center justify-center mb-5 shadow-[0_0_30px_rgba(52,200,120,0.22)]">
                <Rocket className="w-7 h-7 text-[hsl(var(--primary))]" />
              </div>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                {plan.name} — 3-Day Free Trial
              </h2>
              <p className="text-muted-foreground mt-2 text-sm max-w-sm">
                Get full access to all {plan.name} features for 3 days absolutely free.
              </p>
            </div>

            <div className="mt-7 space-y-3">
              <a
                href="#/contact"
                className="group flex items-center gap-4 p-4 rounded-xl bg-background/60 border border-border hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-all"
                data-testid="trial-contact-btn"
              >
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
                  <Headphones className="w-5 h-5" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="font-display font-bold text-base">Get Free Trial</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Contact support to activate your 3-day trial</div>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-all shrink-0" />
              </a>

              <button
                onClick={onPayNow}
                className="w-full group flex items-center gap-4 p-4 rounded-xl bg-background/60 border border-border hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-all text-left"
                data-testid="trial-paynow-btn"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] via-cyan-400 to-purple-500 flex items-center justify-center text-black shrink-0 shadow-[0_0_18px_rgba(52,200,120,0.35)]">
                  <Wallet className="w-5 h-5" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="font-display font-bold text-base">Pay Now — {plan.priceLabel}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Skip the trial and start immediately</div>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>
            </div>

            <p className="text-[11px] text-center text-muted-foreground mt-6">
              Trial includes all {plan.name} features. No credit card required.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
